import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorHandler } from '../../helper/error_handler';
import { isErrorWithMessage } from '../../helper';
import { resettingSubscriptionLoader } from '../slices/subscription';
import { RootState } from '../store';
import { settingBillingEnabledFlag } from '../slices';
import SubscriptionApi from '../../apis/subscription';

export const getPlansThunk = createAsyncThunk(
    'subscription/plans',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SubscriptionApi.getPlans(state.app.token);
            if (response?.success === 1) {
                return response.plans;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSubscriptionLoader());
        }
    }
);

export const checkoutPlanThunk = createAsyncThunk(
    'subscription/checkout',
    async (plan_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SubscriptionApi.checkoutPlan(state.app.token, plan_id);
            if (response?.success === 1) {
                return response;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSubscriptionLoader());
        }
    }
);

export const specialUpgradePlanThunk = createAsyncThunk(
    'subscription/special-upgrade',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SubscriptionApi.specialUpgradePlan(state.app.token);
            if (response?.success === 1) {
                return response;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSubscriptionLoader());
        }
    }
);

export const cancelSubscriptionThunk = createAsyncThunk(
    'subscription/cancel',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SubscriptionApi.cancelSubscription(state.app.token);
            if (response?.success === 1) {
                dispatch(settingBillingEnabledFlag(false));
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSubscriptionLoader());
        }
    }
);

export const changePlanThunk = createAsyncThunk(
    'subscription/change',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SubscriptionApi.changePlan(state.app.token, state.subscription.selectedPlan?.id || 0);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Updating stage order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSubscriptionLoader());
        }
    }
);

export const fetchProratedChargeThunk = createAsyncThunk(
    'subscription/prorated',
    async (id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SubscriptionApi.fetchProratedCharge(state.app.token, id);
            if (response?.success === 1) {
                return response?.prorated_amount;
            }
            return errorHandler(response?.msg as string || 'Updating deal order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSubscriptionLoader());
        }
    }
);

export const fetchCurrentPlanDetailsThunk = createAsyncThunk(
    'subscription/details',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await SubscriptionApi.fetchCurrentPlanDetails(state.app.token);
            if (response?.success === 1) {
                return {
                    subscription: {
                        ...(response?.subscription || {}),
                        plan: response.plan,
                        discount_msg: response.discount_msg,
                        remaining_days: response.remaining_days,
                        discount_percent: response.discount_percent
                    },
                    invoices: response?.invoices
                };
            }
            return errorHandler(response?.msg as string || 'Updating deal order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingSubscriptionLoader());
        }
    }
);